import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { calendarApi, customerApi } from '../api';
import BookingInfoModal from '../components/BookingInfoModal';
import FormModal from '../components/FormModal';
import { IconChevronLeft, IconDots, IconEdit, IconTag } from '../components/icons';
import {
  Empty, ErrorNote, Modal, Spinner, dateOf, dateTimeOf, money, th, todayIso, useLoader,
} from '../components/common';

const GENDER_TH = { female: 'หญิง', male: 'ชาย', other: 'อื่น ๆ', unspecified: 'ไม่ระบุ' };

/** 0649576077 → 064-957-6077. Their detail page groups it; the list does not. */
const phoneTh = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length !== 10) return raw || '-';
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Whole years, counted off the date string rather than a Date: a birthday is a
 * calendar day, and reading it as an instant would age a customer by a year for
 * anyone west of the shop.
 */
const ageOf = (birthDate) => {
  if (!birthDate) return null;
  const [by, bm, bd] = String(birthDate).slice(0, 10).split('-').map(Number);
  const [y, m, d] = todayIso().split('-').map(Number);
  const years = y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
  return years >= 0 ? years : null;
};

function Field({ label, value }) {
  return (
    <div className="dd-field">
      <span className="dd-label">{label}</span>
      <span className="dd-value">{value}</span>
    </div>
  );
}

/** The count badge their ประวัติ rows put in front of รายละเอียด and หมอนวด. */
function Count({ value }) {
  if (!value) return null;
  return <span className="badge badge-grey" style={{ marginRight: 6 }}>{value}</span>;
}

/**
 * รายละเอียดลูกค้า — one member.
 *
 * Three columns over a full-width ประวัติ block, whose สาขา column is there
 * because the history is deliberately every branch's: customers are the
 * organisation's, so what they spent at Song Wat belongs on the same page as
 * what they spent at Sukhumvit.
 */
export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState('orders');
  const [booking, setBooking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [preferredOpen, setPreferredOpen] = useState(false);

  const detail = useLoader(() => customerApi.get(id), [id]);

  if (detail.loading) return <Spinner />;
  if (detail.error) return <div className="card card-pad"><ErrorNote error={detail.error} /></div>;

  const {
    customer, requirements, packages, visits, stats, regulars, therapists = [],
  } = detail.data;
  const latestNote = requirements[0];
  const age = ageOf(customer.birthDate);

  return (
    <>
      <div className="toolbar">
        <button className="icon-square" title="ย้อนกลับ" onClick={() => navigate('/customers')}>
          <IconChevronLeft size={18} />
        </button>
        <h2 style={{ margin: 0 }}>รายละเอียดลูกค้า</h2>
        <div style={{ flex: 1 }} />
        {/* English on their page too, alone among the buttons — kept as theirs. */}
        <button className="btn-solid" onClick={() => setBooking(true)}>Book</button>
      </div>

      <div className="cust-cols">
        <div className="cust-stats">
          <div className="stat-card">
            <div className="label">ยอดรวมการซื้อ</div>
            <div className="value">{money(stats.totalSpend)}</div>
            <div className="cust-split">
              <div><span>บริการ</span><strong>{money(stats.serviceSpend)}</strong></div>
              <div><span>สินค้า</span><strong>{money(stats.productSpend)}</strong></div>
            </div>
          </div>
          <div className="stat-card">
            <div className="label">ยอดซื้อแพ็กเกจ</div>
            <div className="value">{money(stats.packageSpend)}</div>
          </div>
          <div className="stat-card">
            <div className="label">จำนวนครั้งที่มา</div>
            <div className="value">{stats.visitCount}</div>
          </div>
        </div>

        <div className="card card-pad">
          <div className="cust-head">
            <strong>{customer.nickname || customer.firstName || 'ลูกค้า'}</strong>
            <button className="icon-square" title="แก้ไข" onClick={() => setEditing(true)}>
              <IconEdit size={16} />
            </button>
          </div>

          <div className="cust-ident">
            <Field label="รหัสสมาชิก" value={customer.memberCode} />
            <Field label="เบอร์โทรศัพท์" value={phoneTh(customer.phone)} />
            <Field label="เพศ" value={GENDER_TH[customer.gender] || 'ไม่ระบุ'} />
            <Field label="วันเกิด" value={customer.birthDate ? dateOf(customer.birthDate) : '-'} />
            {/* Their example reads ไม่ระบุ, not '-', for both of these. */}
            <Field label="อายุ" value={age !== null ? `${age} ปี` : customer.ageBandLabel || 'ไม่ระบุ'} />
            <Field label="สัญชาติ" value={customer.nationalityNameTh || 'ไม่ระบุ'} />
          </div>

          <div className="cust-note">
            <span className="dd-label">ความต้องการพิเศษล่าสุด</span>
            <button
              className="btn-ghost btn-sm"
              disabled={!requirements.length}
              onClick={() => setNotesOpen(true)}
            >
              ดูทั้งหมด
            </button>
            <span className="dd-value">{latestNote ? latestNote.note : '-'}</span>
            <span className="dd-label">{latestNote ? dateOf(latestNote.created_at) : ''}</span>
          </div>

          <div className="cust-note">
            <span className="dd-label">คงเหลือ</span>
            <span className="dd-value">{stats.remainingPackages} แพ็กเกจ</span>
          </div>
        </div>

        <div className="card card-pad">
          <div className="cust-pref-head">
            <div className="dd-label">หมอนวดประจำ</div>
            <button
              className="icon-square"
              title="เลือกหมอนวดประจำ"
              onClick={() => setPreferredOpen(true)}
            >
              <IconEdit size={15} />
            </button>
          </div>
          <div className={`cust-well${regulars.therapist ? ' on' : ''}`}>
            {regulars.therapist ? regulars.therapist.name : 'ยังไม่มีหมอนวดประจำ'}
          </div>
          <div className="dd-label">บริการประจำ</div>
          <div className={`cust-well${regulars.service ? ' on' : ''}`}>
            {regulars.service ? regulars.service.name : 'ยังไม่มีบริการประจำ'}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-pad" style={{ paddingBottom: 0 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>ประวัติ</h3>
          <div className="tab-row">
            <button
              className={`tab${tab === 'orders' ? ' active' : ''}`}
              onClick={() => setTab('orders')}
            >
              คำสั่งซื้อ
            </button>
            <button
              className={`tab${tab === 'packages' ? ' active' : ''}`}
              onClick={() => setTab('packages')}
            >
              แพ็กเกจ
            </button>
          </div>
        </div>

        {(tab === 'orders' ? visits : packages).length === 0
          ? <Empty icon={tab === 'orders' ? 'history' : 'product'} />
          : (
          <table className="data">
            <thead>
              <tr>
                {/* _note: the ••• is a column of its own before รหัสออเดอร์ on
                    their page, but what the menu opens was never captured, so
                    it is drawn and does nothing rather than invent entries. */}
                <th aria-label="เมนู" />
                <th>รหัสออเดอร์</th>
                <th>สาขา</th>
                <th>วัน-เวลา</th>
                <th>ประเภท</th>
                <th>ชื่อ</th>
                <th>เบอร์โทรศัพท์</th>
                <th>รายละเอียด</th>
                <th>หมอนวด</th>
              </tr>
            </thead>
            <tbody>
              {tab === 'orders' ? visits.map((v) => (
                <tr key={v.order_id}>
                  <td className="cust-rowmenu"><IconDots size={16} /></td>
                  <td style={{ fontWeight: 600 }}>#{v.order_code}</td>
                  <td>{v.branch_name}</td>
                  <td>{dateTimeOf(v.opened_at)} น.</td>
                  <td><span className="badge badge-green tag"><IconTag size={13} /> {th(v.order_type)}</span></td>
                  <td>{v.customer_name || 'ลูกค้าไม่ระบุชื่อ'}</td>
                  <td>{phoneTh(v.customer_phone)}</td>
                  <td className="cust-summary">
                    <Count value={v.item_count} />{v.item_summary || '-'}
                  </td>
                  <td><Count value={v.therapist_count} />{v.therapist_names || '-'}</td>
                </tr>
              )) : packages.map((p) => (
                <tr key={p.id}>
                  <td className="cust-rowmenu"><IconDots size={16} /></td>
                  <td style={{ fontWeight: 600 }}>{p.order_code ? `#${p.order_code}` : '-'}</td>
                  <td>{p.branch_name}</td>
                  <td>{dateTimeOf(p.purchased_at)} น.</td>
                  <td><span className="badge badge-green tag"><IconTag size={13} /> {th('package')}</span></td>
                  <td>{customer.nickname || customer.firstName || 'ลูกค้าไม่ระบุชื่อ'}</td>
                  <td>{phoneTh(customer.phone)}</td>
                  <td className="cust-summary">
                    <Count value={p.item_count} />{p.name_th || p.name_en}
                  </td>
                  {/* A package is sold over the counter, so nobody performed it. */}
                  <td>-</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {booking && (
        <BookingInfoModal
          customer={customer}
          onClose={() => setBooking(false)}
          onNext={(info) => navigate('/book', { state: { info } })}
        />
      )}

      {editing && (
        <EditCustomer
          customer={customer}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); detail.reload(); }}
        />
      )}

      {notesOpen && (
        <Modal title="ความต้องการพิเศษ" onClose={() => setNotesOpen(false)}>
          {requirements.map((r) => (
            <div key={r.id} className="summary-row">
              <span>{r.note}</span>
              <span>{dateOf(r.created_at)}</span>
            </div>
          ))}
        </Modal>
      )}

      {preferredOpen && (
        <PreferredTherapistModal
          customer={customer}
          therapists={therapists}
          currentId={regulars.therapist?.is_preferred ? regulars.therapist.id : null}
          onClose={() => setPreferredOpen(false)}
          onSaved={() => {
            setPreferredOpen(false);
            detail.reload();
          }}
        />
      )}
    </>
  );
}

function PreferredTherapistModal({
  customer, therapists, currentId, onClose, onSaved,
}) {
  const [therapistId, setTherapistId] = useState(currentId ? String(currentId) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await customerApi.setPreferredTherapist(
        customer.id,
        therapistId ? Number(therapistId) : null,
      );
      onSaved();
    } catch (saveError) {
      setError(saveError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="เลือกหมอนวดประจำ" onClose={onClose}>
      {error && <ErrorNote error={error} />}
      <label className="field">
        <span>หมอนวด</span>
        <select value={therapistId} onChange={(e) => setTherapistId(e.target.value)}>
          <option value="">ไม่กำหนด</option>
          {therapists.map((therapist) => (
            <option key={therapist.id} value={therapist.id}>
              {therapist.name} ({therapist.code})
            </option>
          ))}
        </select>
      </label>
      <div className="modal-actions">
        <div style={{ flex: 1 }} />
        <button className="btn" disabled={busy} onClick={onClose}>ยกเลิก</button>
        <button className="btn btn-solid" disabled={busy} onClick={save}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}

/** The pencil on the identity card. Same fields สร้างลูกค้า collects. */
function EditCustomer({ customer, onClose, onSaved }) {
  const lookups = useLoader(() => calendarApi.lookups(), []);

  const fields = [
    { name: 'nickname', label: 'ชื่อเล่น' },
    { name: 'phone', label: 'เบอร์โทรศัพท์' },
    { name: 'firstName', label: 'ชื่อจริง' },
    { name: 'lastName', label: 'นามสกุล' },
    {
      name: 'gender',
      label: 'เพศ',
      type: 'select',
      placeholder: 'ไม่ระบุ',
      options: ['female', 'male', 'other'].map((g) => ({ value: g, label: GENDER_TH[g] })),
    },
    { name: 'birthDate', label: 'วันเกิด', type: 'date' },
    {
      name: 'nationalityCode',
      label: 'สัญชาติ',
      type: 'select',
      placeholder: 'ไม่ระบุ',
      options: (lookups.data?.nationalities || []).map((n) => ({
        value: n.code, label: n.name_th || n.name_en,
      })),
    },
    {
      name: 'occupationId',
      label: 'อาชีพ',
      type: 'select',
      placeholder: 'ไม่ระบุ',
      options: (lookups.data?.occupations || []).map((o) => ({
        value: String(o.id), label: o.name_th || o.name_en,
      })),
    },
  ];

  if (lookups.loading) return <Modal title="แก้ไขลูกค้า" onClose={onClose}><Spinner /></Modal>;

  return (
    <FormModal
      title="แก้ไขลูกค้า"
      fields={fields}
      initial={{
        nickname: customer.nickname || '',
        phone: customer.phone || '',
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        gender: customer.gender === 'unspecified' ? '' : customer.gender,
        birthDate: String(customer.birthDate || '').slice(0, 10),
        nationalityCode: customer.nationalityCode || '',
        occupationId: customer.occupationId ? String(customer.occupationId) : '',
      }}
      onClose={onClose}
      onSubmit={async (values) => {
        // A cleared field is null, never '': birth_date and the reference ids
        // are typed columns that reject a blank string. gender is NOT NULL, and
        // its blank means the customer was never asked.
        const patch = Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, v === '' ? null : v]),
        );
        await customerApi.update(customer.id, { ...patch, gender: patch.gender || 'unspecified' });
        onSaved();
      }}
    />
  );
}
